
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, Download, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ProfileReviewUsageStat {
  user_id: string;
  user_email: string;
  review_count: number;
  last_reviewed: string;
  upwork_profile_url: string | null;
}

const ProfileReviewUsageAdmin = () => {
  const [usageStats, setUsageStats] = useState<ProfileReviewUsageStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    const fetchUsageStats = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase.rpc('get_profile_review_usage_stats');

        if (error) {
          throw error;
        }

        setUsageStats(data || []);
        
        // Calculate total reviews
        const total = data?.reduce((sum: number, item: ProfileReviewUsageStat) => sum + item.review_count, 0) || 0;
        setTotalCount(total);
      } catch (error) {
        console.error('Error fetching profile review usage stats:', error);
        setError(error.message || 'Failed to fetch usage statistics');
        toast({
          title: "Error",
          description: "Failed to load profile review usage data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsageStats();
  }, [toast]);

  const exportCsv = () => {
    if (!usageStats.length) return;

    // Format CSV header
    const headers = Object.keys(usageStats[0]).join(",");
    // Format CSV rows
    const rows = usageStats.map(row => 
      Object.values(row).map(value => 
        typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
      ).join(",")
    ).join("\n");
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "profile_review_usage.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <Card className="bg-destructive/10">
        <CardContent className="pt-6">
          <p className="text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Review Usage Statistics</CardTitle>
        <CardDescription>
          Track how many profile reviews users have generated with the AI tool
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[250px]" />
                  <Skeleton className="h-4 w-[200px]" />
                </div>
              </div>
            ))}
          </div>
        ) : usageStats.length === 0 ? (
          <p className="text-muted-foreground">No profile review usage data available.</p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardHeader className="py-4">
                  <CardTitle className="text-base font-medium">Total Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{usageStats.length}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="py-4">
                  <CardTitle className="text-base font-medium">Total Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{totalCount}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="py-4">
                  <CardTitle className="text-base font-medium">Avg. Per User</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {usageStats.length ? (totalCount / usageStats.length).toFixed(1) : '0'}
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end mb-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportCsv}
                disabled={!usageStats.length}
                className="flex items-center gap-1"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User Email</TableHead>
                  <TableHead>Reviews Generated</TableHead>
                  <TableHead>Last Generated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usageStats.map((stat) => (
                  <TableRow key={stat.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage 
                            src={`https://avatar.vercel.sh/${stat.user_email}`} 
                            alt={stat.user_email} 
                          />
                          <AvatarFallback>
                            {stat.user_email.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span>{stat.user_email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {stat.review_count}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(stat.last_reviewed), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell className="text-right">
                      {stat.upwork_profile_url && (
                        <a 
                          href={stat.upwork_profile_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 hover:underline mr-2"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Profile
                        </a>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>View details</DropdownMenuItem>
                          <DropdownMenuItem>Export data</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileReviewUsageAdmin;
